import { createFrontendModule } from '@backstage/frontend-plugin-api';
import {
  createFormField,
  FormFieldBlueprint,
} from '@backstage/plugin-scaffolder-react/alpha';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import {
  isStackSpecEditable,
  statusLabel,
} from '@internal/plugin-stack-common';
import type { StackLastOperation } from '@internal/plugin-stack-common';
import { scaffolderPageExtension } from './templateFormRoute';

// Skill: using-backstage-ui

/**
 * A module on the scaffolder rather than a plugin of its own: the form fields
 * below only exist to be collected by the scaffolder's `api:scaffolder/
 * form-fields`, and overriding one of the scaffolder's own extensions - which
 * is what the restyled form page does - is something only a module can do.
 */
export const scaffolderModule = createFrontendModule({
  pluginId: 'scaffolder',
  extensions: [
    scaffolderPageExtension,
    FormFieldBlueprint.make({
      name: 'secret',
      params: {
        field: async () => {
          const { SecretField } = await import('./fields/SecretField');
          return createFormField({
            name: 'Secret',
            component: SecretField,
            schema: makeFieldSchema({
              output: zImpl => zImpl.string(),
            }),
          });
        },
      },
    }),
    FormFieldBlueprint.make({
      name: 'policy-lifecycle-definition',
      params: {
        field: async () => {
          const { PolicyLifecycleDefinitionField } = await import(
            './fields/PolicyLifecycleDefinitionField'
          );
          return createFormField({
            name: 'PolicyLifecycleDefinition',
            component: PolicyLifecycleDefinitionField,
            schema: makeFieldSchema({
              output: z =>
                z.object({
                  definition: z.record(z.unknown()).optional(),
                  stages: z.array(z.string()).optional(),
                  operations: z.array(z.string()).optional(),
                  returnToAudit: z.boolean().optional(),
                }),
              uiOptions: z => z.object({ kind: z.enum(['cel', 'quota']) }),
            }),
          });
        },
      },
    }),
    FormFieldBlueprint.make({
      name: 'policy-resource-picker',
      params: {
        field: async () => {
          const { PolicyResourcePickerField } = await import(
            './fields/PolicyResourcePickerField'
          );
          return createFormField({
            name: 'PolicyResourcePicker',
            component: PolicyResourcePickerField,
            schema: makeFieldSchema({
              output: z => z.string(),
              uiOptions: z =>
                z.object({
                  tenant: z.string().optional(),
                  resource: z.enum(['binding', 'policy']),
                  kind: z.enum(['cel', 'quota']).optional(),
                  unboundOnly: z.boolean().optional(),
                }),
            }),
          });
        },
      },
    }),
    FormFieldBlueprint.make({
      name: 'identity-subject-picker',
      params: {
        field: async () => {
          const { IdentitySubjectPickerField } = await import(
            './fields/IdentitySubjectPickerField'
          );
          return createFormField({
            name: 'IdentitySubjectPicker',
            component: IdentitySubjectPickerField,
            schema: makeFieldSchema({
              output: zImpl =>
                zImpl.object({
                  type: zImpl.enum(['user', 'group']),
                  id: zImpl.string(),
                  displayRef: zImpl.string(),
                }),
              uiOptions: zImpl =>
                zImpl.object({
                  tenant: zImpl.string().optional(),
                  kinds: zImpl
                    .array(zImpl.enum(['User', 'Group']))
                    .nonempty()
                    .optional(),
                }),
            }),
          });
        },
      },
    }),
    FormFieldBlueprint.make({
      name: 'stack-parameters',
      params: {
        field: async () => {
          const { StackParametersField } = await import(
            './fields/StackParametersField'
          );

          return createFormField({
            name: 'StackParameters',
            component: StackParametersField,
            validation: (data, field) => {
              const lifecycleState = data?.lifecycleState;
              if (
                lifecycleState &&
                !isStackSpecEditable({
                  lifecycleState: String(lifecycleState),
                  lastOperation: data?.lastOperation as
                    | StackLastOperation
                    | undefined,
                })
              ) {
                const name = statusLabel(
                  String(data?.status ?? lifecycleState),
                );
                field.addError(`Stack status ${name} cannot be updated`);
              }
            },
            schema: makeFieldSchema({
              output: zImpl =>
                zImpl.object({
                  entityRef: zImpl.string().optional(),
                  name: zImpl.string().optional(),
                  template: zImpl.string().optional(),
                  stackType: zImpl.string().optional(),
                  lifecycleState: zImpl.string().optional(),
                  lastOperation: zImpl.record(zImpl.unknown()).optional(),
                  status: zImpl.string().optional(),
                  parameters: zImpl.record(zImpl.unknown()).optional(),
                }),
              uiOptions: zImpl =>
                zImpl.object({
                  entityRef: zImpl.string().optional(),
                  entityRefQueryParam: zImpl.string().optional(),
                  templateName: zImpl.string().optional(),
                  overwrite: zImpl.boolean().optional(),
                  clearable: zImpl.array(zImpl.string()).optional(),
                  fields: zImpl.array(zImpl.string()).optional(),
                  pickers: zImpl.record(zImpl.string()).optional(),
                }),
            }),
          });
        },
      },
    }),
    FormFieldBlueprint.make({
      name: 'application-group-picker',
      params: {
        field: async () => {
          const { ApplicationGroupPickerField } = await import(
            './fields/ApplicationGroupPickerField'
          );

          return createFormField({
            name: 'ApplicationGroupPicker',
            component: ApplicationGroupPickerField,
            schema: makeFieldSchema({
              output: zImpl => zImpl.string(),
              uiOptions: zImpl =>
                zImpl.object({
                  tenant: zImpl.string().optional(),
                  value: zImpl.enum(['name', 'path', 'ref']).optional(),
                  includeRetired: zImpl.boolean().optional(),
                }),
            }),
          });
        },
      },
    }),
    FormFieldBlueprint.make({
      name: 'policy-definition',
      params: {
        field: async () => {
          const { PolicyDefinitionField } = await import(
            './fields/PolicyDefinitionField'
          );
          return createFormField({
            name: 'PolicyDefinition',
            component: PolicyDefinitionField,
            schema: makeFieldSchema({
              output: zImpl =>
                zImpl.object({
                  policy_id: zImpl.string().optional(),
                  expected_revision: zImpl.string().optional(),
                  rule_id: zImpl.string().optional(),
                  cel_expression: zImpl.string().optional(),
                  failure_message: zImpl.string().optional(),
                  max_cpu_vcpu: zImpl.number().optional(),
                  limit_vcpu: zImpl.number().optional(),
                }),
              uiOptions: zImpl =>
                zImpl.object({
                  kind: zImpl.enum(['cel', 'quota']),
                  tenant: zImpl.string().optional(),
                }),
            }),
          });
        },
      },
    }),
    FormFieldBlueprint.make({
      name: 'destination-picker',
      params: {
        field: async () => {
          const { DestinationPickerField } = await import(
            './fields/DestinationPickerField'
          );

          return createFormField({
            name: 'DestinationPicker',
            component: DestinationPickerField,
            schema: makeFieldSchema({
              output: zImpl => zImpl.string(),
              uiOptions: zImpl =>
                zImpl.object({
                  tenant: zImpl.string().optional(),
                  template: zImpl.string(),
                }),
            }),
          });
        },
      },
    }),
    FormFieldBlueprint.make({
      name: 'load-balancer-public-ip-picker',
      params: {
        field: async () => {
          const { LoadBalancerPublicIPPickerField } = await import(
            './fields/LoadBalancerResourcePickerFields'
          );
          return createFormField({
            name: 'LoadBalancerPublicIPPicker',
            component: LoadBalancerPublicIPPickerField,
            schema: makeFieldSchema({
              output: zImpl => zImpl.string(),
              uiOptions: zImpl =>
                zImpl.object({
                  tenant: zImpl.string().optional(),
                  applicationGroup: zImpl.string().optional(),
                  destination: zImpl.string().optional(),
                }),
            }),
          });
        },
      },
    }),
    FormFieldBlueprint.make({
      name: 'load-balancer-machine-picker',
      params: {
        field: async () => {
          const { LoadBalancerMachinePickerField } = await import(
            './fields/LoadBalancerResourcePickerFields'
          );
          return createFormField({
            name: 'LoadBalancerMachinePicker',
            component: LoadBalancerMachinePickerField,
            schema: makeFieldSchema({
              output: zImpl => zImpl.array(zImpl.string()),
              uiOptions: zImpl =>
                zImpl.object({
                  tenant: zImpl.string().optional(),
                  applicationGroup: zImpl.string().optional(),
                  destination: zImpl.string().optional(),
                  publicIPID: zImpl.string().optional(),
                }),
            }),
          });
        },
      },
    }),
    FormFieldBlueprint.make({
      name: 'ssh-keypair-stack-picker',
      params: {
        field: async () => {
          const { SshKeypairStackPickerField } = await import(
            './fields/SshKeypairStackPickerField'
          );
          return createFormField({
            name: 'SshKeypairStackPicker',
            component: SshKeypairStackPickerField,
            schema: makeFieldSchema({
              output: zImpl => zImpl.string(),
              uiOptions: zImpl =>
                zImpl.object({
                  tenant: zImpl.string().optional(),
                  applicationGroup: zImpl.string().optional(),
                  destination: zImpl.string().optional(),
                }),
            }),
          });
        },
      },
    }),
    FormFieldBlueprint.make({
      name: 'cloudstack-offering-picker',
      params: {
        field: async () => {
          const { CloudStackOfferingPickerField } = await import(
            './fields/CloudStackOfferingPickerField'
          );

          return createFormField({
            name: 'CloudStackOfferingPicker',
            component: CloudStackOfferingPickerField,
            schema: makeFieldSchema({
              output: zImpl => zImpl.string(),
              uiOptions: zImpl =>
                zImpl.object({
                  tenant: zImpl.string().optional(),
                  destination: zImpl.string().optional(),
                  allowCustomSizing: zImpl.boolean().optional(),
                }),
            }),
          });
        },
      },
    }),
    FormFieldBlueprint.make({
      name: 'cloudstack-image-picker',
      params: {
        field: async () => {
          const { CloudStackImagePickerField } = await import(
            './fields/CloudStackImagePickerField'
          );

          return createFormField({
            name: 'CloudStackImagePicker',
            component: CloudStackImagePickerField,
            schema: makeFieldSchema({
              output: zImpl => zImpl.string(),
              uiOptions: zImpl =>
                zImpl.object({
                  tenant: zImpl.string().optional(),
                  destination: zImpl.string().optional(),
                }),
            }),
          });
        },
      },
    }),
    FormFieldBlueprint.make({
      name: 'cloudstack-network-offering-picker',
      params: {
        field: async () => {
          const { CloudStackNetworkOfferingPickerField } = await import(
            './fields/CloudStackNetworkOfferingPickerField'
          );

          return createFormField({
            name: 'CloudStackNetworkOfferingPicker',
            component: CloudStackNetworkOfferingPickerField,
            schema: makeFieldSchema({
              output: zImpl => zImpl.string(),
              uiOptions: zImpl =>
                zImpl.object({
                  tenant: zImpl.string().optional(),
                  destination: zImpl.string().optional(),
                  catalogue: zImpl.enum(['vpc', 'tier']).optional(),
                }),
            }),
          });
        },
      },
    }),
    FormFieldBlueprint.make({
      name: 'cloudstack-network-picker',
      params: {
        field: async () => {
          const { CloudStackNetworkPickerField } = await import(
            './fields/CloudStackNetworkPickerField'
          );

          return createFormField({
            name: 'CloudStackNetworkPicker',
            component: CloudStackNetworkPickerField,
            schema: makeFieldSchema({
              output: zImpl =>
                zImpl.union([zImpl.string(), zImpl.array(zImpl.string())]),
              uiOptions: zImpl =>
                zImpl.object({
                  tenant: zImpl.string().optional(),
                  applicationGroup: zImpl.string().optional(),
                  ownedOnly: zImpl.boolean().optional(),
                  tiersOnly: zImpl.boolean().optional(),
                  destination: zImpl.string().optional(),
                }),
            }),
          });
        },
      },
    }),
    FormFieldBlueprint.make({
      name: 'kubernetes-version-picker',
      params: {
        field: async () => {
          const { KubernetesVersionPickerField } = await import(
            './fields/KubernetesVersionPickerField'
          );

          return createFormField({
            name: 'KubernetesVersionPicker',
            component: KubernetesVersionPickerField,
            schema: makeFieldSchema({
              output: zImpl => zImpl.string(),
              uiOptions: zImpl =>
                zImpl.object({
                  versions: zImpl
                    .array(
                      zImpl.object({
                        id: zImpl.string(),
                        name: zImpl.string(),
                      }),
                    )
                    .optional(),
                }),
            }),
          });
        },
      },
    }),
    FormFieldBlueprint.make({
      name: 'tenant-picker',
      params: {
        field: async () => {
          const { TenantPickerField } = await import(
            './fields/TenantPickerField'
          );

          return createFormField({
            name: 'TenantPicker',
            component: TenantPickerField,
            schema: makeFieldSchema({
              output: zImpl => zImpl.string(),
            }),
          });
        },
      },
    }),
  ],
});
